import * as React from "react";
import { find, head, map, size } from "lodash";
import { Scrollbars } from "react-custom-scrollbars";
import styled from "styled-components";
import { transparentize } from "@glossy";
import { Color } from "@glossy/colors";
import { content } from "@glossy/theme";
import { Locale } from "@linkfluence/models/locale";
import { natural } from "@tootools/sort";
import Icon from "@ui-kit/icon";
import WorkspacesList from "@radarly/components/navigation/switcher/workspaces";
import { tr } from "@radarly/i18n";
import { Project } from "@radarly/models/project";
import { User } from "@radarly/models/user";
import { Workspace } from "@radarly/models/workspace";
import { IRouterService } from "@radarly/shared/router";
import Pill from "./pill";

const Container = styled.div<{ height: number }>`
  border-top: solid 1px ${(props) => content(props, "tertiary")};
  max-height: ${(props) => props.height + "px"};
`;

const Left = styled.div`
  flex: 250px 0 0;
  width: 250px;
`;

const Right = styled.div`
  flex-grow: 1;
  border-left: solid 1px ${(props) => content(props, "tertiary")};
`;

const Header = styled.div`
  border-bottom: solid 1px ${(props) => content(props, "tertiary")};
  height: 42px;
  font-weight: bold;
  font-size: 14px;
  line-height: 42px;
`;

const HeaderLabel = styled.div`
  padding: 2px 16px;
`;

const Content = styled.div`
  display: flex;
`;

const StyledProject = styled.div<{ active: boolean }>`
  width: 100%;
  cursor: pointer;
  font-size: 14px;
  height: 46px;
  line-height: 46px;
  padding: 0 16px;
  overflow: hidden;
  background-color: ${(props) =>
    props.active ? transparentize(0.85, content(props)) : null};
  font-weight: ${(props) => (props.active ? "bold" : null)};

  &:hover {
    background: ${(props) => transparentize(0.85, content(props))};
  }
`;

const Arrow = styled(Icon)`
  height: 46px;
`;

const Status = styled(Icon)`
  height: 46px;
  margin-left: 5px;
`;

const ProjectLabel = styled.div`
  display: inline-block;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: calc(100% - 16px);
  vertical-align: middle;
  overflow: hidden;
`;

const CenterDrawing = styled(Icon)`
  margin: 25px 0 25px 248px;
`;

interface IProps {
  projects: Project[];
  onChange: (project: number) => void;
  children?: React.ReactNode;
  project: number;
  user: User;
  workspaces?: Workspace[];
  workspaceId?: number;
  locale: Locale;
  clickBlockedProjectHelp?: () => void;
  height?: number;
  loading?: boolean;
  onClickWorkspace?: (workspaceId: number) => void;
  router: IRouterService;
}

const NavigationSwitcherProjects = ({
  projects = [],
  project,
  onChange,
  workspaces,
  workspaceId,
  clickBlockedProjectHelp,
  locale,
  height = 300,
  loading,
  user,
  onClickWorkspace,
  router,
}: IProps) => {
  const currentProject = find(projects, { id: project }) || head(projects);
  const sizeProjects = size(projects);

  return (
    <Container height={height}>
      {sizeProjects ? (
        sizeProjects === 1 ? (
          <>
            <Content>
              <Scrollbars autoHeight autoHeightMax={height - 42}>
                <WorkspacesList
                  workspaces={workspaces}
                  project={currentProject}
                  workspaceId={workspaceId}
                  locale={locale}
                  loading={loading}
                  clickBlockedProjectHelp={clickBlockedProjectHelp}
                  onClickWorkspace={onClickWorkspace}
                  user={user}
                  router={router}
                  withSingleProjectHeader
                />
              </Scrollbars>
            </Content>
          </>
        ) : (
          <>
            <Header>
              <Left>
                <HeaderLabel>
                  {tr(locale, "SWITCHER::HEADER_PROJECTS")}
                </HeaderLabel>
              </Left>
            </Header>
            <Content>
              <Left>
                <Scrollbars autoHeight autoHeightMax={height - 42}>
                  {map(natural(projects, "label"), (prj) => (
                    <StyledProject
                      onClick={() => onChange(prj.id)}
                      active={project === prj.id}
                      key={prj.id}
                    >
                      <ProjectLabel>
                        {prj.label}
                        {!prj.flags.isAccessible && (
                          <Status glyph="disc-close" color={Color.Ruby} />
                        )}
                        {prj.flags.isAccessible && !prj.flags.isActive && (
                          <Status
                            glyph="disc-pause"
                            color={Color.DarkTangerine}
                          />
                        )}
                      </ProjectLabel>
                      {project === prj.id && <Arrow glyph="chevron-right" />}
                    </StyledProject>
                  ))}
                </Scrollbars>
              </Left>
              <Right>
                <Scrollbars autoHeight autoHeightMax={height - 42}>
                  <WorkspacesList
                    workspaces={workspaces}
                    project={currentProject}
                    workspaceId={workspaceId}
                    locale={locale}
                    loading={loading}
                    clickBlockedProjectHelp={clickBlockedProjectHelp}
                    onClickWorkspace={onClickWorkspace}
                    user={user}
                    router={router}
                  />
                </Scrollbars>
              </Right>
            </Content>
          </>
        )
      ) : (
        <>
          <Pill
            label={tr(locale, "SWITCHER::PROJECTS_EMPTY")}
            color={Color.DimGrey}
          />
          <CenterDrawing glyph="ghost" size={104} />
        </>
      )}
    </Container>
  );
};

export default NavigationSwitcherProjects;
